import type {
  CampaignPatient,
  AudiencePreview,
  Campaign,
  CreateCampaignInput,
  RecipientPreset,
  SendCampaignResult,
  UpdateCampaignInput,
} from '@/application/campaigns/campaignsTypes';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

interface ErrorResponse {
  error?: string;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    return body.error ?? 'campaigns_request_failed';
  } catch {
    return 'campaigns_request_failed';
  }
}

async function requestJson<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as T;
}

export function listCampaigns(token: string): Promise<Campaign[]> {
  return requestJson<Campaign[]>(token, '/communications');
}

export function getCampaign(token: string, id: string): Promise<Campaign> {
  return requestJson<Campaign>(token, `/communications/${encodeURIComponent(id)}`);
}

export function createCampaign(token: string, input: CreateCampaignInput): Promise<Campaign> {
  return requestJson<Campaign>(token, '/communications', { method: 'POST', body: JSON.stringify(input) });
}

export function updateCampaign(token: string, id: string, input: UpdateCampaignInput): Promise<Campaign> {
  return requestJson<Campaign>(token, `/communications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function previewAudience(
  token: string,
  input: { clinicId: string; segmentPresets: RecipientPreset[]; manualPatientIds: number[]; sampleSize?: number }
): Promise<AudiencePreview> {
  return requestJson<AudiencePreview>(token, '/communications/preview', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function sendCampaign(token: string, id: string): Promise<SendCampaignResult> {
  return requestJson<SendCampaignResult>(token, `/communications/${encodeURIComponent(id)}/send`, { method: 'POST' });
}

export function searchPatients(
  token: string,
  params: { clinicId: string; q?: string; onlyActive?: boolean; limit?: number }
): Promise<CampaignPatient[]> {
  const search = new URLSearchParams({ clinicId: params.clinicId });
  if (params.q?.trim()) {
    search.set('q', params.q.trim());
  }
  if (params.onlyActive === false) {
    search.set('onlyActive', 'false');
  }
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  return requestJson<CampaignPatient[]>(token, `/communications/patients?${search.toString()}`);
}
